// Shared carousel/video behaviour for sections/key-features-new.liquid.
// Runs once per page load and initialises every instance of the section found
// on the page (originally this was duplicated inline per section instance,
// scoped via a Liquid-interpolated #shopify-section-{{ section.id }} selector).
(() => {
  const sections = document.querySelectorAll('.premium-mower-showcase-inner');
  if (!sections.length) return;

  sections.forEach((section) => {
    const viewport = section.querySelector('[data-premium-mower-viewport]');
    const prevButtons = Array.from(section.querySelectorAll('[data-premium-mower-prev], [data-premium-mower-mobile-prev]'));
    const nextButtons = Array.from(section.querySelectorAll('[data-premium-mower-next], [data-premium-mower-mobile-next]'));
    const progressBar = section.querySelector('[data-premium-mower-progress]');

    if (!viewport) return;

    const cardVideos = Array.from(section.querySelectorAll('.premium-mower-card__video'));

    const prepareCardVideo = (video) => {
      if (!video) return;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
    };

    const playCardVideo = (video) => {
      if (!video || document.hidden) return;

      prepareCardVideo(video);

      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    };

    const getVisibleCardVideos = () => {
      if (!cardVideos.length || document.hidden) return [];

      const viewportRect = viewport.getBoundingClientRect();

      return cardVideos.filter((video) => {
        const rect = video.getBoundingClientRect();
        return rect.width > 0 &&
          rect.height > 0 &&
          rect.right > viewportRect.left &&
          rect.left < viewportRect.right &&
          rect.bottom > 0 &&
          rect.top < window.innerHeight;
      });
    };

    const playVisibleCardVideos = () => {
      getVisibleCardVideos().forEach((video) => {
        playCardVideo(video);
      });
    };

    const restartVisibleCardVideos = () => {
      getVisibleCardVideos().forEach((video) => {
        prepareCardVideo(video);

        try {
          video.pause();
        } catch (error) {}

        window.requestAnimationFrame(() => {
          playCardVideo(video);
        });
      });
    };

    cardVideos.forEach((video) => {
      prepareCardVideo(video);

      video.addEventListener('loadeddata', () => playCardVideo(video));
      video.addEventListener('canplay', () => playCardVideo(video));
    });

    if ('IntersectionObserver' in window && cardVideos.length) {
      const cardVideoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            playCardVideo(entry.target);
          }
        });
      }, {
        root: viewport,
        threshold: 0.15
      });

      cardVideos.forEach((video) => cardVideoObserver.observe(video));
    }

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        playVisibleCardVideos();
      }
    });

    window.addEventListener('pageshow', playVisibleCardVideos);
    window.addEventListener('load', playVisibleCardVideos);

    const getCards = () => Array.from(viewport.querySelectorAll('.premium-mower-card'));

    const getScrollAmount = () => {
      const card = viewport.querySelector('.premium-mower-card');
      const track = viewport.querySelector('.premium-mower-showcase__track');
      if (!card) return viewport.clientWidth * 0.85;

      const styles = track ? window.getComputedStyle(track) : null;
      const gap = styles ? parseFloat(styles.columnGap || styles.gap || '0') : 0;
      return card.offsetWidth + gap;
    };

    const isMobileLayout = () => window.matchMedia('(max-width: 767px)').matches;

    const getViewportStartOffset = () => {
      // Same value the track uses for its padding-left (the page's real container
      // inset), read live so it always matches the current viewport width.
      const rawValue = window.getComputedStyle(section).getPropertyValue('--premium-mower-container-inset');
      const parsedValue = parseFloat(rawValue || '0');
      return Number.isFinite(parsedValue) ? parsedValue : 0;
    };

    const getCardTargetLeft = (card) => {
      if (!card) return 0;
      return card.offsetLeft - getViewportStartOffset();
    };

    const getActiveIndex = () => {
      const cards = getCards();
      if (!cards.length) return 0;

      let closestIndex = 0;
      let closestDistance = Infinity;

      cards.forEach((card, index) => {
        const distance = Math.abs(getCardTargetLeft(card) - viewport.scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    };

    const scrollToIndex = (index, behavior = 'smooth') => {
      const cards = getCards();
      if (!cards.length) return;

      const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const safeIndex = ((index % cards.length) + cards.length) % cards.length;
      const target = Math.min(maxScroll, Math.max(0, getCardTargetLeft(cards[safeIndex])));

      viewport.scrollTo({
        left: target,
        behavior
      });
    };

    const updateControls = () => {
      const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const scrollLeft = viewport.scrollLeft;

      if (progressBar) {
        const progress = maxScroll === 0 ? 1 : Math.min(1, Math.max(0, scrollLeft / maxScroll));
        progressBar.style.transform = `scaleX(${progress})`;
      }

      const isMobile = window.matchMedia('(max-width: 767px)').matches;

      prevButtons.forEach((button) => {
        if (!button) return;
        button.disabled = !isMobile && scrollLeft <= 4;
      });

      nextButtons.forEach((button) => {
        if (!button) return;
        button.disabled = !isMobile && scrollLeft >= maxScroll - 4;
      });
    };

    const moveCarousel = (direction) => {
      const isMobile = window.matchMedia('(max-width: 767px)').matches;

      if (isMobile) {
        scrollToIndex(getActiveIndex() + direction);
        return;
      }

      const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const target = Math.min(maxScroll, Math.max(0, viewport.scrollLeft + getScrollAmount() * direction));

      viewport.scrollTo({
        left: target,
        behavior: 'smooth'
      });
    };

    prevButtons.forEach((button) => button.addEventListener('click', () => moveCarousel(-1)));
    nextButtons.forEach((button) => button.addEventListener('click', () => moveCarousel(1)));

    let isDown = false;
    let startX = 0;
    let startY = 0;
    let scrollStart = 0;
    let moved = false;
    let lockedHorizontal = false;
    let suppressClick = false;
    const interactiveSelector = 'a, button, input, textarea, select, label, summary, [role="button"]';

    viewport.addEventListener('pointerdown', (event) => {
      if (isMobileLayout()) return;
      if (event.button !== undefined && event.button !== 0) return;

      if (event.target.closest(interactiveSelector)) {
        isDown = false;
        moved = false;
        lockedHorizontal = false;
        suppressClick = false;
        return;
      }

      isDown = true;
      moved = false;
      lockedHorizontal = false;
      startX = event.clientX;
      startY = event.clientY;
      scrollStart = viewport.scrollLeft;
    });

    viewport.addEventListener('pointermove', (event) => {
      if (isMobileLayout()) return;
      if (!isDown) return;

      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (!lockedHorizontal) {
        if (absY > 10 && absY > absX) return;
        if (absX <= 6 || absX <= absY) return;

        lockedHorizontal = true;
        moved = true;
        viewport.classList.add('is-dragging');
        try { viewport.setPointerCapture(event.pointerId); } catch (error) {}
      }

      if (event.cancelable) event.preventDefault();
      viewport.scrollLeft = scrollStart - deltaX;
    });

    const endDrag = (event) => {
      if (!isDown) return;
      isDown = false;
      suppressClick = moved;
      viewport.classList.remove('is-dragging');
      try { viewport.releasePointerCapture(event.pointerId); } catch (error) {}

      if (moved && !isMobileLayout()) {
        window.requestAnimationFrame(updateControls);
      }

      window.setTimeout(() => {
        moved = false;
        lockedHorizontal = false;
        suppressClick = false;
      }, 180);
    };

    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('pointerleave', endDrag);

    viewport.addEventListener('click', (event) => {
      if (suppressClick) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    }, true);

    viewport.addEventListener('dragstart', (event) => {
      event.preventDefault();
    });

    viewport.addEventListener('scroll', () => {
      updateControls();
      window.requestAnimationFrame(playVisibleCardVideos);
    }, { passive: true });

    let resizeVideoTimer = null;
    let resizeVideoLongTimer = null;

    const handleResizeVideoPlayback = () => {
      updateControls();
      window.requestAnimationFrame(playVisibleCardVideos);

      window.clearTimeout(resizeVideoTimer);
      window.clearTimeout(resizeVideoLongTimer);

      resizeVideoTimer = window.setTimeout(() => {
        updateControls();
        restartVisibleCardVideos();
      }, 250);

      resizeVideoLongTimer = window.setTimeout(() => {
        updateControls();
        restartVisibleCardVideos();
      }, 700);
    };

    window.addEventListener('resize', handleResizeVideoPlayback);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResizeVideoPlayback);
    }

    requestAnimationFrame(updateControls);
    requestAnimationFrame(playVisibleCardVideos);
    window.setTimeout(playVisibleCardVideos, 400);
    window.setTimeout(playVisibleCardVideos, 1200);
  });
})();
